/**
 * Typed wrappers for the Payroll CSV Bridge module (G5.3 / G5.4 / G5.5).
 * Paths and shapes are taken VERBATIM from
 * backend/internal/router/router.go and the handlers.PayrollHandler /
 * handlers.TenantSettingsHandler routes.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). The /payroll routes are
 * NOT feature-gated (foundational HR per plan §7); they ARE RBAC'd to
 * company_admin / hr_manager server-side on ALL endpoints (read AND
 * write — no employee-self read in v1; payslips are G5.5-salary-slip).
 *
 * No client-supplied tenant_id / user_id is ever sent: the server derives
 * both from the session for every route.
 *
 * Security-critical step-up TOTP route surfaced but NOT special-cased
 * here (the response interceptor / caller handles the totp_required 401):
 *   - POST /payroll/runs/{id}/lock   (irreversible — Companies Act §128(1))
 */

import { api, blobErrorToApi } from "./client";
import { filenameFromDisposition, namedBlob } from "@/lib/download-filename";
import type {
  CreateCorrectionRunRequest,
  CreateCorrectionRunResponse,
  CreateSalaryRevisionRequest,
  CreateSalaryRevisionResponse,
  PayrollSettings,
  PayrollSetup,
  SalarySplit,
  SalaryBreakupResponse,
  PreviousEmploymentResponse,
  PutPreviousEmploymentRequest,
  OpeningYTDResponse,
  PutOpeningYTDRequest,
  SalaryArrearsResponse,
  RemoveCorrectionEmployeeResponse,
  CompRevisionLetter,
  CompRevisionLetterResponse,
  CreateCompRevisionLetterRequest,
  RecoverSalaryArrearResponse,
  SalaryArrearResolution,
  SalaryRevisionsResponse,
  CreatePayrollRunRequest,
  DiscardPayrollRunResponse,
  EmployeePayrollSettings,
  PayrollPeriodWarnings,
  PFCeilingReport,
  CalculateStatutoryBonusRequest,
  StatutoryBonusResponse,
  UpdateStatutoryBonusRowRequest,
  UpdateEmployeePayrollSettingsRequest,
  ListMyPayslipsResponse,
  ListPayrollRunsResponse,
  PayrollLockResponse,
  PayrollRun,
  PayrollRunResponse,
  RecalculatePayrollRunResponse,
  TenantSettings,
  UpdateTenantSettingsRequest,
  UpsertOverrideRequest,
} from "@/types/payroll";

export const PAYROLL_BASE = "/api/v1/payroll";
export const TENANT_SETTINGS_BASE = "/api/v1/tenant/settings";

// ---------------------------------------------------------------------------
// Payroll runs
// ---------------------------------------------------------------------------

/**
 * GET /payroll/runs?year=
 *
 * HR-admin only. Returns every run for the tenant (optionally filtered
 * by year), most recent first. Snapshot rows are EMBEDDED in each
 * payrollRunDTO — for tenants with hundreds of employees this can be a
 * large payload; v1.5 may add a `compact=true` variant.
 */
// GET /api/v1/payroll/setup — what is missing before a run can be created.
export async function getPayrollSetup(signal?: AbortSignal): Promise<PayrollSetup> {
  const { data } = await api.get<PayrollSetup>(`${PAYROLL_BASE}/setup`, { signal });
  return data;
}

/** "Start from annual CTC" — GET /payroll/salary-split?ctc_inr= (rupees). */
export async function getSalarySplit(ctcInr: string, signal?: AbortSignal): Promise<SalarySplit> {
  const { data } = await api.get<SalarySplit>(`${PAYROLL_BASE}/salary-split`, {
    params: { ctc_inr: ctcInr },
    signal,
  });
  return data;
}

/** My salary — GET /payroll/me/salary (self only). */
export async function getMySalary(signal?: AbortSignal): Promise<SalaryBreakupResponse> {
  const { data } = await api.get<SalaryBreakupResponse>(`${PAYROLL_BASE}/me/salary`, { signal });
  return data;
}

/** People → Pay — GET /payroll/employees/{id}/salary (HR). */
export async function getEmployeeSalary(
  employeeId: number,
  signal?: AbortSignal,
): Promise<SalaryBreakupResponse> {
  const { data } = await api.get<SalaryBreakupResponse>(
    `${PAYROLL_BASE}/employees/${employeeId}/salary`,
    { signal },
  );
  return data;
}

export async function listRuns(
  params?: { year?: number },
  signal?: AbortSignal,
): Promise<ListPayrollRunsResponse> {
  const { data } = await api.get<ListPayrollRunsResponse>(
    `${PAYROLL_BASE}/runs`,
    {
      params: params?.year != null ? { year: params.year } : undefined,
      signal,
    },
  );
  return data;
}

/**
 * GET /payroll/me/payslips — the caller's OWN salary slips from LOCKED
 * runs, newest first (Phase 1a — employee self-service "My Pay").
 *
 * Any authenticated user (no role gate): the backend derives the
 * employee from the session and returns ONLY that person's slips. No
 * client-supplied id is sent. Each row carries enough to render the
 * period label + net pay and to drive the existing per-slip PDF
 * download (run_id + the current user's own employee id).
 */
export async function listMyPayslips(
  signal?: AbortSignal,
): Promise<ListMyPayslipsResponse> {
  const { data } = await api.get<ListMyPayslipsResponse>(
    `${PAYROLL_BASE}/me/payslips`,
    { signal },
  );
  return data;
}

/**
 * POST /payroll/runs — create a new run for (year, month). The backend
 * seeds the snapshot by computing every active employee + writes the
 * generic CSV blob in one TX.
 *
 * Sentinels (handler → wire):
 *  - 409 run_already_open  — an open draft already exists for this
 *                            period; HR must lock or open the existing
 *                            draft (we show a "Open existing draft" CTA).
 *  - 422 inputs_missing    — at least one active employee lacks a
 *                            required input (basic salary, regime, etc.);
 *                            the verbatim message names the offending
 *                            user_id + field (caller surfaces it).
 *  - 400 validation        — year/month out of range.
 */
export async function createRun(
  body: CreatePayrollRunRequest,
): Promise<PayrollRun> {
  const { data } = await api.post<PayrollRunResponse>(
    `${PAYROLL_BASE}/runs`,
    body,
  );
  return data.run;
}

/**
 * GET /payroll/runs/{id} — one run + deserialised snapshot rows. A
 * cross-tenant id is 404 (no oracle).
 */
export async function getRun(
  id: number,
  signal?: AbortSignal,
): Promise<PayrollRun> {
  const { data } = await api.get<PayrollRunResponse>(
    `${PAYROLL_BASE}/runs/${id}`,
    { signal },
  );
  return data.run;
}

/**
 * PATCH /payroll/runs/{id}/employees/{employee_id} — upsert one row's
 * HR overrides AND recompute + persist the snapshot row + regenerate the
 * cached generic_csv in one TX. Returns the full updated run snapshot
 * so the caller can replace local state without a separate refetch.
 *
 * Sentinels:
 *  - 404 not_found            — run not in this tenant
 *  - 409 run_already_locked   — corrections require a new run
 *  - 422 inputs_missing       — employee's salary structure incomplete
 *  - 422 validation           — per-field range (each magnitude in
 *                               [0, ₹100 cr]; lop_days in [0, 99])
 */
export async function upsertOverride(
  runId: number,
  employeeId: number,
  body: UpsertOverrideRequest,
): Promise<PayrollRun> {
  const { data } = await api.patch<PayrollRunResponse>(
    `${PAYROLL_BASE}/runs/${runId}/employees/${employeeId}`,
    body,
  );
  return data.run;
}

/**
 * POST /payroll/runs/{id}/recalculate — refresh an UNLOCKED draft:
 * re-reads who is employed in the period (joiners/leavers), salary
 * structures, leave + attendance LOP, loan EMIs and ESI coverage, and
 * recomputes every row while keeping HR's explicit overrides. Returns
 * the full updated run plus any explicit advance recovery it reverted to
 * automatic because the loan it was meant for has been closed.
 *
 * Sentinels: 404 not_found · 409 run_already_locked · 422 inputs_missing.
 */
export async function recalculateRun(
  id: number,
): Promise<RecalculatePayrollRunResponse> {
  const { data } = await api.post<RecalculatePayrollRunResponse>(
    `${PAYROLL_BASE}/runs/${id}/recalculate`,
    {},
  );
  return normalizeRecalculateResponse(data);
}

/** The recalculate body with every list present (recalculate, and the
 *  skip / include-again endpoints that answer with the same shape). */
function normalizeRecalculateResponse(
  data: RecalculatePayrollRunResponse,
): RecalculatePayrollRunResponse {
  return {
    run: data.run,
    dropped_advance_recoveries: data.dropped_advance_recoveries ?? [],
    dropped_employee_ids: data.dropped_employee_ids ?? [],
    ineligible_dropped: data.ineligible_dropped ?? [],
  };
}

/**
 * DELETE /payroll/runs/{id}/employees/{employee_id} — remove ONE employee
 * from an OPEN correction draft (their difference row and any amount added
 * for them); everyone else's differences stay.
 *
 * Sentinels: 404 not_found · 409 run_already_locked · 409
 * employee_not_in_run · 422 remove_employee_regular_run · 422
 * correction_last_employee (discard the draft instead).
 */
export async function removeRunEmployee(
  runId: number,
  employeeId: number,
): Promise<RemoveCorrectionEmployeeResponse> {
  const { data } = await api.delete<RemoveCorrectionEmployeeResponse>(
    `${PAYROLL_BASE}/runs/${runId}/employees/${employeeId}`,
  );
  return data;
}

/**
 * DELETE /payroll/runs/{id} — discard an UNLOCKED draft (+ its
 * overrides). A locked run is never deleted (409 run_already_locked).
 * Referral bonuses paid into the draft return to pending.
 */
export async function discardRun(
  id: number,
): Promise<DiscardPayrollRunResponse> {
  const { data } = await api.delete<DiscardPayrollRunResponse>(
    `${PAYROLL_BASE}/runs/${id}`,
  );
  return data;
}

/**
 * POST /payroll/runs/{id}/lock — irreversible immutability stamp
 * (Companies Act §128(1)). Step-up TOTP gated server-side: an
 * unenrolled HR caller lands on 403 totp_enrollment_required at the
 * middleware boundary; an enrolled-but-not-stepped-up session 401s
 * with `totp_required` (caller routes to /2fa-setup or /2fa-verify).
 *
 * Sentinels:
 *  - 409 run_already_locked   — race/idempotency
 *  - 422 net_negative         — at least one row has net < 0; the
 *                               response body lists offending ids
 *  - 409 run_stale_recalculate — the draft no longer matches its inputs
 *  - 422 advance_recovery_exceeds_loan — a recovery larger than the EMI
 *                               due on the employee's open loans
 *  - 409 tds_table_missing    — automatic TDS could not be calculated (no
 *                               income-tax table for the FY); employee_ids
 *
 * A successful lock may carry non-blocking `lock_warnings`.
 */
export async function lockRun(
  id: number,
  opts?: { acknowledgeMonthNotEnded?: boolean },
): Promise<PayrollLockResponse> {
  const { data } = await api.post<PayrollLockResponse>(
    `${PAYROLL_BASE}/runs/${id}/lock`,
    opts?.acknowledgeMonthNotEnded ? { acknowledge_month_not_ended: true } : {},
  );
  return data;
}

/**
 * POST /payroll/runs/{id}/corrections — create a correction
 * (supplementary) draft from a LOCKED regular run for the selected
 * employees. Each is recomputed on current data; the draft holds the
 * DIFFERENCE from what the period already paid. Locks through lockRun.
 *
 * Sentinels: 404 not_found · 409 run_already_open · 409
 * correction_parent_superseded · 422 correction_parent_not_locked /
 * correction_parent_not_regular / validation / correction_employees_ineligible
 * (+ employees) / inputs_missing (+ employees).
 */
export async function createCorrectionRun(
  parentRunId: number,
  body: CreateCorrectionRunRequest,
): Promise<CreateCorrectionRunResponse> {
  const { data } = await api.post<CreateCorrectionRunResponse>(
    `${PAYROLL_BASE}/runs/${parentRunId}/corrections`,
    body,
  );
  return { ...data, unchanged_employee_ids: data.unchanged_employee_ids ?? [] };
}

/** GET /payroll/period-warnings — periods holding two locked full runs. */
export async function getPeriodWarnings(
  signal?: AbortSignal,
): Promise<PayrollPeriodWarnings> {
  const { data } = await api.get<PayrollPeriodWarnings>(
    `${PAYROLL_BASE}/period-warnings`,
    { signal },
  );
  return { double_counted_periods: data.double_counted_periods ?? [] };
}

/** GET /payroll/employees/{id}/settings — payroll exclusion + PT state. */
export async function getEmployeePayrollSettings(
  employeeId: number,
  signal?: AbortSignal,
): Promise<EmployeePayrollSettings> {
  const { data } = await api.get<EmployeePayrollSettings>(
    `${PAYROLL_BASE}/employees/${employeeId}/settings`,
    { signal },
  );
  return data;
}

/** PATCH /payroll/employees/{id}/settings — 422 validation on an unknown
 *  PT state. */
export async function updateEmployeePayrollSettings(
  employeeId: number,
  body: UpdateEmployeePayrollSettingsRequest,
): Promise<EmployeePayrollSettings> {
  const { data } = await api.patch<EmployeePayrollSettings>(
    `${PAYROLL_BASE}/employees/${employeeId}/settings`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Wave 2 (migrations 113/114) — salary history, arrears, previous employer,
// payroll settings. HR-admin only, like every /payroll route.
// ---------------------------------------------------------------------------

/** GET /payroll/employees/{id}/salary-revisions — history (newest first),
 *  the salary in force today, and unpaid arrears for locked months. */
export async function listSalaryRevisions(
  employeeId: number,
  signal?: AbortSignal,
): Promise<SalaryRevisionsResponse> {
  const { data } = await api.get<SalaryRevisionsResponse>(
    `${PAYROLL_BASE}/employees/${employeeId}/salary-revisions`,
    { signal },
  );
  return {
    ...data,
    revisions: data.revisions ?? [],
    arrears: data.arrears ?? [],
  };
}

/**
 * Overpaid salary arrears (F13). recover: add the overpayment as "Other
 * deductions" to an open REGULAR draft for a later month (the arrear leaves
 * the list once that draft is locked with it). dismiss: waive it with a
 * mandatory note. reopen: undo a dismissal.
 *
 * Sentinels: 404 not_found · 409 arrear_already_resolved · 422
 * arrear_not_overpaid / arrear_recovery_run_invalid / validation (note).
 */
export async function recoverSalaryArrear(body: {
  employee_id: number;
  parent_run_id: number;
  run_id: number;
}): Promise<RecoverSalaryArrearResponse> {
  const { data } = await api.post<RecoverSalaryArrearResponse>(
    `${PAYROLL_BASE}/salary-arrears/recover`,
    body,
  );
  return data;
}

export async function dismissSalaryArrear(body: {
  employee_id: number;
  parent_run_id: number;
  note: string;
}): Promise<SalaryArrearResolution> {
  const { data } = await api.post<SalaryArrearResolution>(
    `${PAYROLL_BASE}/salary-arrears/dismiss`,
    body,
  );
  return data;
}

export async function reopenSalaryArrear(body: {
  employee_id: number;
  parent_run_id: number;
}): Promise<SalaryArrearResolution> {
  const { data } = await api.post<SalaryArrearResolution>(
    `${PAYROLL_BASE}/salary-arrears/reopen`,
    body,
  );
  return data;
}

/** POST /payroll/employees/{id}/salary-revisions — 422 validation (basic
 *  required and > 0; YYYY-MM-DD date), 404 unknown employee. */
export async function createSalaryRevision(
  employeeId: number,
  body: CreateSalaryRevisionRequest,
): Promise<CreateSalaryRevisionResponse> {
  const { data } = await api.post<CreateSalaryRevisionResponse>(
    `${PAYROLL_BASE}/employees/${employeeId}/salary-revisions`,
    body,
  );
  return { ...data, arrears: data.arrears ?? [] };
}

/** GET /payroll/pf-ceiling/affected — who was short-deducted PF in months
 *  locked before the ₹25,000 ceiling (17 Sep 2026), and who has PF off
 *  while earning ₹15,000–₹25,000. Estimates; HR-admin. */
export async function getPFCeilingAffected(
  signal?: AbortSignal,
): Promise<PFCeilingReport> {
  const { data } = await api.get<PFCeilingReport>(
    `${PAYROLL_BASE}/pf-ceiling/affected`,
    { signal },
  );
  return {
    months: (data.months ?? []).map((m) => ({
      ...m,
      people: (m.people ?? []).map((p) => ({ ...p, owed: p.owed ?? null })),
      recovery_run: m.recovery_run ?? null,
      owed_outstanding_paise: m.owed_outstanding_paise ?? 0,
    })),
    pf_off: data.pf_off ?? [],
    uncorrected_people: data.uncorrected_people ?? 0,
    owed_people: data.owed_people ?? 0,
    recovering_people: data.recovering_people ?? 0,
  };
}

/** POST /payroll/pf-ceiling/owed/recover — add the PF a correction left
 *  owed (up to the deduction limit) as Other deductions to the open regular
 *  draft for a later month, when the automatic step could not (e.g. the
 *  draft was edited since). carried_paise did not fit and stays owed.
 *  409 no_open_run · already_scheduled · deduction_limit · not_in_run ·
 *  nothing_owed; 403 own_record. */
export async function recoverPFOwed(body: {
  correction_run_id: number;
  employee_id: number;
}): Promise<{ run_id: number; period_year: number; period_month: number; amount_paise: number; carried_paise: number }> {
  const { data } = await api.post<{
    run_id: number;
    period_year: number;
    period_month: number;
    amount_paise: number;
    carried_paise: number;
  }>(`${PAYROLL_BASE}/pf-ceiling/owed/recover`, body);
  return data;
}

/** POST /payroll/pf-ceiling/owed/waive — the company bears it (note
 *  required); a part in an open draft is taken back out first. */
export async function waivePFOwed(body: {
  correction_run_id: number;
  employee_id: number;
  note: string;
}): Promise<{ amount_paise: number }> {
  const { data } = await api.post<{ amount_paise: number }>(`${PAYROLL_BASE}/pf-ceiling/owed/waive`, body);
  return data;
}

// ---------------------------------------------------------------------------
// Statutory bonus (Code on Wages Ch. IV) — /payroll/statutory-bonus
// ---------------------------------------------------------------------------

function normaliseStatutoryBonus(data: StatutoryBonusResponse): StatutoryBonusResponse {
  return {
    ...data,
    open_run: data.open_run ?? null,
    calculation: data.calculation ? { ...data.calculation, rows: data.calculation.rows ?? [] } : null,
  };
}

/** GET /payroll/statutory-bonus — the FY (default: the last one ended). */
export async function getStatutoryBonus(fy?: number, signal?: AbortSignal): Promise<StatutoryBonusResponse> {
  const { data } = await api.get<StatutoryBonusResponse>(`${PAYROLL_BASE}/statutory-bonus`, {
    params: fy ? { fy } : undefined,
    signal,
  });
  return normaliseStatutoryBonus(data);
}

/** POST /payroll/statutory-bonus/calculate */
export async function calculateStatutoryBonus(body: CalculateStatutoryBonusRequest): Promise<StatutoryBonusResponse> {
  const { data } = await api.post<StatutoryBonusResponse>(`${PAYROLL_BASE}/statutory-bonus/calculate`, body);
  return normaliseStatutoryBonus(data);
}

/** PATCH /payroll/statutory-bonus/{id}/employees/{employee_id} — exclude, or wages for earlier months. */
export async function updateStatutoryBonusRow(
  id: number,
  employeeId: number,
  body: UpdateStatutoryBonusRowRequest,
): Promise<StatutoryBonusResponse> {
  const { data } = await api.patch<StatutoryBonusResponse>(
    `${PAYROLL_BASE}/statutory-bonus/${id}/employees/${employeeId}`,
    body,
  );
  return normaliseStatutoryBonus(data);
}

/** POST /payroll/statutory-bonus/{id}/apply — add to the open payroll draft. */
export async function applyStatutoryBonus(id: number): Promise<StatutoryBonusResponse> {
  const { data } = await api.post<StatutoryBonusResponse>(`${PAYROLL_BASE}/statutory-bonus/${id}/apply`);
  return normaliseStatutoryBonus(data);
}

/** GET /payroll/statutory-bonus/{id}/csv */
export async function downloadStatutoryBonusCSV(id: number): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${PAYROLL_BASE}/statutory-bonus/${id}/csv`, { responseType: "blob" });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/** GET /payroll/pf-ceiling/affected.csv — the same rows as a spreadsheet. */
export async function downloadPFCeilingCSV(): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${PAYROLL_BASE}/pf-ceiling/affected.csv`, {
      responseType: "blob",
    });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/** GET /payroll/salary-arrears — every unpaid difference for locked months. */
export async function listSalaryArrears(
  signal?: AbortSignal,
): Promise<SalaryArrearsResponse> {
  const { data } = await api.get<SalaryArrearsResponse>(
    `${PAYROLL_BASE}/salary-arrears`,
    { signal },
  );
  return { items: data.items ?? [] };
}

/** GET /payroll/employees/{id}/previous-employment?fy= (FY start year;
 *  omitted = the current FY). */
export async function getPreviousEmployment(
  employeeId: number,
  fy?: number,
  signal?: AbortSignal,
): Promise<PreviousEmploymentResponse> {
  const { data } = await api.get<PreviousEmploymentResponse>(
    `${PAYROLL_BASE}/employees/${employeeId}/previous-employment`,
    { params: fy ? { fy } : undefined, signal },
  );
  return data;
}

/** PUT /payroll/employees/{id}/previous-employment?fy= — 422 validation
 *  (TDS above income, malformed TAN, out-of-range amounts). */
export async function putPreviousEmployment(
  employeeId: number,
  fy: number,
  body: PutPreviousEmploymentRequest,
): Promise<PreviousEmploymentResponse> {
  const { data } = await api.put<PreviousEmploymentResponse>(
    `${PAYROLL_BASE}/employees/${employeeId}/previous-employment`,
    body,
    { params: { fy } },
  );
  return data;
}

/** DELETE /payroll/employees/{id}/previous-employment?fy= — 404 when none. */
export async function deletePreviousEmployment(
  employeeId: number,
  fy: number,
): Promise<void> {
  await api.delete(`${PAYROLL_BASE}/employees/${employeeId}/previous-employment`, {
    params: { fy },
  });
}

/** GET /payroll/employees/{id}/opening-ytd?fy= — salary paid by this
 *  employer before payroll moved to WorkRight (omitted fy = current FY). */
export async function getOpeningYTD(
  employeeId: number,
  fy?: number,
  signal?: AbortSignal,
): Promise<OpeningYTDResponse> {
  const { data } = await api.get<OpeningYTDResponse>(
    `${PAYROLL_BASE}/employees/${employeeId}/opening-ytd`,
    { params: fy ? { fy } : undefined, signal },
  );
  return data;
}

/** PUT /payroll/employees/{id}/opening-ytd?fy= — 422 validation (month
 *  outside the FY, amounts), 409 overlaps_locked_payroll (a month already in
 *  locked WorkRight payroll for the employee). */
export async function putOpeningYTD(
  employeeId: number,
  fy: number,
  body: PutOpeningYTDRequest,
): Promise<OpeningYTDResponse> {
  const { data } = await api.put<OpeningYTDResponse>(
    `${PAYROLL_BASE}/employees/${employeeId}/opening-ytd`,
    body,
    { params: { fy } },
  );
  return data;
}

/** DELETE /payroll/employees/{id}/opening-ytd?fy= — 404 when none. */
export async function deleteOpeningYTD(employeeId: number, fy: number): Promise<void> {
  await api.delete(`${PAYROLL_BASE}/employees/${employeeId}/opening-ytd`, {
    params: { fy },
  });
}

/** GET /payroll/settings — "calculate TDS automatically". */
export async function getPayrollSettings(
  signal?: AbortSignal,
): Promise<PayrollSettings> {
  const { data } = await api.get<PayrollSettings>(`${PAYROLL_BASE}/settings`, {
    signal,
  });
  return data;
}

/** PATCH /payroll/settings — applies to rows computed or recalculated
 *  afterwards; open drafts become stale until recalculated. */
export async function updatePayrollSettings(
  body: PayrollSettings,
): Promise<PayrollSettings> {
  const { data } = await api.patch<PayrollSettings>(
    `${PAYROLL_BASE}/settings`,
    body,
  );
  return data;
}

/**
 * GET /payroll/runs/{id}/csv?format=generic|razorpay_x — streams the
 * cached CSV bytes. Post-lock only; pre-lock the snapshot is still
 * mutable so the backend 422s `run_not_locked`. We fetch as a blob and
 * let the caller decide what to do with it (the offboarding letter
 * download pattern); blobErrorToApi rehydrates the JSON envelope so a
 * 404/422 keeps its message instead of degrading to neutral generic.
 */
export async function downloadCSV(
  id: number,
  format: "generic" | "razorpay_x",
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${PAYROLL_BASE}/runs/${id}/csv`,
      {
        params: { format },
        responseType: "blob",
      },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /payroll/runs/{id}/tally.xml — the run's salary journal voucher as
 * TallyPrime-importable XML (CR0.2). Post-lock only; pre-lock the
 * snapshot is still mutable so the backend 422s `run_not_locked`
 * (404 `not_found` for a cross-tenant / unknown id). Mirrors
 * downloadCSV: blob + blobErrorToApi so the sentinel message survives.
 */
export async function downloadTallyJV(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${PAYROLL_BASE}/runs/${id}/tally.xml`,
      {
        responseType: "blob",
      },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /payroll/runs/{runId}/employees/{employeeId}/slip.pdf — per-
 * employee monthly salary slip (G5.5 — Payment of Wages Act 1936 §13A).
 *
 * Authz: HR-admin OR the employee themselves. Post-lock only —
 * pre-lock the snapshot is still mutable and the backend refuses
 * with 422 `run_not_locked`. Default disposition is `inline` (open in
 * tab via blob URL); `?download=1` flips to `attachment` for the
 * explicit save-to-disk path — pass `{ download: true }` to opt in.
 *
 * Returns a Blob the caller streams to the user (same pattern as the
 * offboarding letter / FF statement download). blobErrorToApi
 * rehydrates the JSON envelope so a 404/422/403 keeps its sentinel
 * code (`run_not_found`, `run_not_locked`, `employee_not_in_run`,
 * `forbidden`) instead of degrading to a neutral generic message.
 *
 * Sentinels:
 *  - 404 run_not_found         — run not in this tenant (cross-tenant
 *                                or unknown id)
 *  - 404 employee_not_in_run   — employee_id absent from the locked
 *                                snapshot rows[] (typo, or row never
 *                                made it into the run)
 *  - 422 run_not_locked        — slip refused before lock
 *  - 403 forbidden             — non-HR caller asking about someone
 *                                else's slip
 */
export async function downloadSalarySlip(
  runId: number,
  employeeId: number,
  opts?: { download?: boolean },
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${PAYROLL_BASE}/runs/${runId}/employees/${employeeId}/slip.pdf`,
      {
        params: opts?.download ? { download: 1 } : undefined,
        responseType: "blob",
      },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * downloadSalarySlip as a file to save: the PDF plus the filename the server
 * chose (Content-Disposition — salary-slip-<YYYY>-<MM>-<employee code>.pdf;
 * the backend exposes the header cross-origin). filename is null when the
 * header could not be read (2026-10 browser QA payroll #22).
 */
export async function downloadSalarySlipFile(
  runId: number,
  employeeId: number,
): Promise<{ blob: Blob; filename: string | null }> {
  try {
    const res = await api.get<Blob>(
      `${PAYROLL_BASE}/runs/${runId}/employees/${employeeId}/slip.pdf`,
      { params: { download: 1 }, responseType: "blob" },
    );
    const header = res.headers?.["content-disposition"];
    return { blob: res.data, filename: filenameFromDisposition(typeof header === "string" ? header : null) };
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/** POST /payroll/runs/{id}/skips — leave employees out of an OPEN regular
 *  draft ("skip this month", QA payroll #21); returns the recalculated run. */
export async function skipRunEmployees(
  runId: number,
  body: { employee_ids: number[]; reason?: string },
): Promise<RecalculatePayrollRunResponse> {
  const { data } = await api.post<RecalculatePayrollRunResponse>(
    `${PAYROLL_BASE}/runs/${runId}/skips`,
    body,
  );
  return normalizeRecalculateResponse(data);
}

/** DELETE /payroll/runs/{id}/skips/{employee_id} — include a skipped
 *  employee again (422 inputs_missing while their record cannot be
 *  computed); returns the recalculated run. */
export async function unskipRunEmployee(
  runId: number,
  employeeId: number,
): Promise<RecalculatePayrollRunResponse> {
  const { data } = await api.delete<RecalculatePayrollRunResponse>(
    `${PAYROLL_BASE}/runs/${runId}/skips/${employeeId}`,
  );
  return normalizeRecalculateResponse(data);
}

// ---------------------------------------------------------------------------
// Statutory exports (G_STAT) — authed blob downloads
// ---------------------------------------------------------------------------
//
// Every statutory register/return is a direct GET that streams text/csv
// (or an XML/JSON return) from LOCKED payroll snapshots (Companies Act
// 2013 §128(1)). The common failure is a 422 with a sentinel envelope
// (`no_locked_runs` / `incomplete_fy` / `run_not_locked`); fetching via
// an `<a href download target=_blank>` rendered that JSON as a raw page
// in a new tab (B-A9). These helpers mirror downloadCSV/downloadSalarySlip
// EXACTLY: authed api client + responseType blob, and blobErrorToApi
// rehydrates the JSON envelope on a non-2xx so the caller can toast the
// sentinel message instead of opening a JSON tab. A blob: URL carries no
// headers, so each helper reads the server's Content-Disposition filename
// itself and attaches it to the returned blob (namedBlob — audit Q27); the
// caller saves with saveBlobAs, whose fallback applies only when the header
// could not be read.
//
// Maternity §11A is the one export NOT under PAYROLL_BASE (it belongs to
// the maternity-cases module); the absolute path is passed through.

const STATUTORY_BASE = "/api/v1/payroll";

// The EPFO ECR is downloaded from Statutory filings (the "#~#" upload file,
// one per month, write-once) — the Statutory page's old CSV export
// (GET /payroll/runs/{id}/ecr.csv: EPS 0.00, wages with paise) disagreed
// with it and is no longer offered (2026-10 re-test payroll #11).

/** GET /payroll/runs/{id}/pt-challan.csv — PT challan for a locked run. */
export async function downloadPTChallan(runId: number): Promise<Blob> {
  try {
    const res = await api.get<Blob>(
      `${STATUTORY_BASE}/runs/${runId}/pt-challan.csv`,
      { params: { download: 1 }, responseType: "blob" },
    );
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /payroll/esi-return.csv?from=&to= — ESI half-yearly return (ESI Act
 * §39). Aggregates every locked run in the window; 422 `no_locked_runs`
 * when the window has none.
 */
export async function downloadESIReturn(
  from: string,
  to: string,
): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${STATUTORY_BASE}/esi-return.csv`, {
      params: { from, to, download: 1 },
      responseType: "blob",
    });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /payroll/bonus-form-d.csv?fy_start=YYYY-04-01 — Bonus Form D annual
 * return (Payment of Bonus Act §26). 422 `incomplete_fy` if any of the
 * 12 months in the FY is not locked.
 */
export async function downloadBonusFormD(fyStart: string): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${STATUTORY_BASE}/bonus-form-d.csv`, {
      params: { fy_start: fyStart, download: 1 },
      responseType: "blob",
    });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /payroll/se-return.csv?year=YYYY — Shops & Establishments annual
 * return for a CALENDAR year (state S&E Acts). 422 `incomplete_year` when a
 * month of the year has no locked run, `no_locked_runs` when none has. (It
 * called GET /payroll/g-stat/s-e?fy= — a route that never existed, so the
 * tile always failed: 2026-10 re-test payroll #4.)
 */
export async function downloadSEReturn(year: number): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${STATUTORY_BASE}/se-return.csv`, {
      params: { year, download: 1 },
      responseType: "blob",
    });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /payroll/g-stat/er?fy=YYYY-YY — Equal Remuneration Form D annual
 * return (ER Act 1976 §8).
 */
export async function downloadERRegister(fy: string): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${STATUTORY_BASE}/g-stat/er`, {
      params: { fy },
      responseType: "blob",
    });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /payroll/g-stat/lwf?year=&period=H1|H2 — LWF biannual contribution
 * (state LWF Acts).
 */
export async function downloadLWFReturn(
  year: number,
  half: 1 | 2,
): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${STATUTORY_BASE}/g-stat/lwf`, {
      params: { year, period: `H${half}` },
      responseType: "blob",
    });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /api/v1/maternity-cases/annual-return?fy=YYYY-YY — Maternity §11A
 * annual return (Maternity Benefit Act §11A). NOT under PAYROLL_BASE —
 * lives in the maternity-cases module — so the absolute path is used.
 */
export async function downloadMaternityReturn(fy: string): Promise<Blob> {
  try {
    const res = await api.get<Blob>(
      "/api/v1/maternity-cases/annual-return",
      { params: { fy }, responseType: "blob" },
    );
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// ---------------------------------------------------------------------------
// Tenant settings (G5.4)
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/tenant/settings — HR-admin only. Returns the workspace
 * configuration carried in tenants (pt_state, state_code, and the POSH
 * allow_public_posh_intake flag; v1.5 may add more). A non-HR caller 403s;
 * the page surfaces that via a role gate.
 */
export async function getTenantSettings(
  signal?: AbortSignal,
): Promise<TenantSettings> {
  const { data } = await api.get<TenantSettings>(TENANT_SETTINGS_BASE, {
    signal,
  });
  return data;
}

/**
 * PATCH /api/v1/tenant/settings — HR-admin only. Mutates one or more
 * settings. Empty-string pt_state means "clear"; null / absent means
 * "leave alone".
 *
 * Sentinels:
 *  - 422 validation         — pt_state not in the 26-state set
 *  - 404 not_found          — tenant id resolved from session is missing
 */
export async function updateTenantSettings(
  body: UpdateTenantSettingsRequest,
): Promise<TenantSettings> {
  const { data } = await api.patch<TenantSettings>(
    TENANT_SETTINGS_BASE,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Compensation revision letters (G10 standalone flow). HR drafts a letter,
// issues it (step-up TOTP — the issued PDF is the §128(1) record and, by
// default, the new salary is applied to payroll from the effective date),
// the employee downloads + acknowledges it. Reads are HR-or-self.
// ---------------------------------------------------------------------------

export const COMP_REVISION_LETTERS_BASE = "/api/v1/comp-revision-letters";

/** GET /comp-revision-letters[?employee_id=] — HR: all (or one employee's);
 *  anyone else: only their own. */
export async function listCompRevisionLetters(
  employeeId?: number,
  signal?: AbortSignal,
): Promise<CompRevisionLetter[]> {
  const { data } = await api.get<{ items: CompRevisionLetter[] }>(COMP_REVISION_LETTERS_BASE, {
    params: employeeId ? { employee_id: employeeId } : undefined,
    signal,
  });
  return data.items ?? [];
}

/** POST /comp-revision-letters — HR drafts (422/400 validation). */
export async function createCompRevisionLetter(
  body: CreateCompRevisionLetterRequest,
): Promise<CompRevisionLetterResponse> {
  const { data } = await api.post<CompRevisionLetterResponse>(COMP_REVISION_LETTERS_BASE, body);
  return data;
}

/** POST /comp-revision-letters/{id}/issue — step-up TOTP (401 totp_required
 *  / 403 totp_enrollment_required); 409 letter_already_issued. */
export async function issueCompRevisionLetter(
  id: number,
  applyToPayroll: boolean,
): Promise<CompRevisionLetterResponse> {
  const { data } = await api.post<CompRevisionLetterResponse>(
    `${COMP_REVISION_LETTERS_BASE}/${id}/issue`,
    { apply_to_payroll: applyToPayroll },
  );
  return data;
}

/** POST /comp-revision-letters/{id}/apply-to-payroll — step-up TOTP;
 *  409 letter_not_issued / letter_already_applied. */
export async function applyCompRevisionLetter(id: number): Promise<CompRevisionLetterResponse> {
  const { data } = await api.post<CompRevisionLetterResponse>(
    `${COMP_REVISION_LETTERS_BASE}/${id}/apply-to-payroll`,
    {},
  );
  return data;
}

/** POST /comp-revision-letters/{id}/acknowledge — the recipient only. */
export async function acknowledgeCompRevisionLetter(id: number): Promise<CompRevisionLetterResponse> {
  const { data } = await api.post<CompRevisionLetterResponse>(
    `${COMP_REVISION_LETTERS_BASE}/${id}/acknowledge`,
    {},
  );
  return data;
}

/** GET /comp-revision-letters/{id}/pdf — issued letters only (409
 *  letter_not_issued). */
export async function downloadCompRevisionLetterPDF(id: number): Promise<Blob> {
  try {
    const res = await api.get<Blob>(`${COMP_REVISION_LETTERS_BASE}/${id}/pdf`, {
      responseType: "blob",
    });
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}
